import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import { getTranslation } from './translations';
import { apiRequest, API_ENDPOINTS } from '../../api/config';

// 首页图标组件
const StatIcon = ({ type }) => {
  if (type === 'cloak') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
      </svg>
    );
  } else if (type === 'log') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    );
  } else if (type === 'subscription') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
      </svg>
    );
  }
  
  return null;
};

// 进度条组件
const ProgressBar = ({ value, max, color }) => {
  const { darkMode } = useTheme();
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="home-progress-bar">
      <div 
        className="home-progress-fill" 
        style={{ 
          width: `${percentage}%`, 
          backgroundColor: darkMode ? '#4FD1C5' : (color || '#38B2AC')
        }}
      ></div>
      <div className="home-progress-text">{percentage}%</div>
    </div>
  );
};

// 状态标签组件
const StatusBadge = ({ isActive, t }) => {
  const { darkMode } = useTheme();
  let badgeClass = "home-status-badge ";
  
  if (isActive) {
    badgeClass += darkMode 
      ? "home-status-active-dark" 
      : "home-status-active";
  } else {
    badgeClass += darkMode 
      ? "home-status-inactive-dark" 
      : "home-status-inactive";
  }
  
  return <span className={badgeClass}>{isActive ? t('active') : t('inactive')}</span>;
};

// 模式标签组件
const ModeBadge = ({ mode, t }) => {
  const { darkMode } = useTheme();
  let badgeClass = "home-mode-badge ";
  let label = "";
  
  switch (mode) {
    case 'cloak':
      badgeClass += darkMode 
        ? "home-mode-cloak-dark" 
        : "home-mode-cloak";
      label = t('cloakMode');
      break;
    case 'fullOpen':
      badgeClass += darkMode 
        ? "home-mode-fullopen-dark" 
        : "home-mode-fullopen";
      label = t('fullOpenMode');
      break;
    case 'audit':
      badgeClass += darkMode 
        ? "home-mode-audit-dark" 
        : "home-mode-audit";
      label = t('auditMode');
      break;
    default:
      badgeClass += darkMode 
        ? "home-mode-default-dark" 
        : "home-mode-default";
      label = t('unknown');
  }
  
  return <span className={badgeClass}>{label}</span>;
};

// Toast组件 - 统一使用斗篷列表风格
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="home-toast">
      {type === 'success' ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="home-toast-icon home-toast-icon-success" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="home-toast-icon home-toast-icon-error" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      )}
      <span>{message}</span>
    </div>
  );
};

// 可复制链接组件
const CopyableLink = ({ url, label, onToast, t }) => {
  const { language, darkMode } = useTheme();
  
  // 备用复制方法 - 使用DOM选择和document.execCommand
  const fallbackCopyToClipboard = (text) => {
    // 创建临时文本区域
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // 确保不会被看到
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      // 执行复制命令，返回成功或失败
      success = document.execCommand('copy');
    } catch (err) {
      console.error(t('copyFailed'), err);
      success = false;
    }
    
    // 清理DOM
    document.body.removeChild(textArea);
    return success;
  };
  
  const copyToClipboard = async (text) => {
    let success = false;
    
    try {
      // 首先尝试使用现代Clipboard API
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
        success = true;
      } else {
        // 如果不支持，使用备用方法
        success = fallbackCopyToClipboard(text);
      }
      
      if (success) {
        // 显示成功提示
        onToast(t('linkCopied'), 'success');
      } else {
        throw new Error(t('copyFailed'));
      }
    } catch (err) {
      console.error(t('copyFailed'), err);
      // 显示错误提示
      onToast(t('copyFailedManual'), 'error');
    }
  };

  if (!url) return <span className="home-link-empty">-</span>;
  
  return (
    <div className="home-copyable-link">
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="home-link-text"
        title={url}
      >
        {label || url}
      </a>
      <button
        onClick={() => copyToClipboard(url)}
        className="home-copy-btn"
        title={t('copyLink')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
        </svg>
      </button>
    </div>
  );
};

const DashboardHome = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalCloaks: 0,
    todayNewCloaks: 0,
    totalLogs: 0,
    subscriptionStatus: '',
    subscriptionExpiry: null,
    totalCalls: 0,
    remainingCalls: 0
  });
  
  const [recentCloaks, setRecentCloaks] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // 显示Toast
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };
  
  // 关闭Toast
  const closeToast = () => {
    setToast({ ...toast, show: false });
  };
  
  // 使用主题上下文
  const { language, darkMode } = useTheme();
  
  // 获取翻译函数
  const t = getTranslation(language);
  
  // 加载数据
  useEffect(() => {
    const loadUserStatistics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 调用后端接口获取统计数据
        const response = await apiRequest(
          API_ENDPOINTS.USER.STATISTICS,
          {},
          true,
          'GET'
        );
        
        if (response.status === 'success' && response.data) {
          const { user, cloak } = response.data;
          
          // 更新统计数据
          setStats({
            totalCloaks: cloak.totalCloaks || 0,
            todayNewCloaks: cloak.todayNewCloaks || 0,
            totalLogs: cloak.totalLogs || 0,
            subscriptionStatus: user.isMember ? t('premiumMember') : t('freeUser'),
            subscriptionExpiry: user.membershipExpiresAt ? new Date(user.membershipExpiresAt) : null,
            totalCalls: user.totalCalls || 0,
            remainingCalls: user.remainingCalls || 0
          });
          
          // 获取最近的斗篷数据
          if (cloak.recentCloaks && Array.isArray(cloak.recentCloaks)) {
            console.log('接收到recentCloaks数据:', cloak.recentCloaks);
            setRecentCloaks(cloak.recentCloaks);
          }
        } else {
          throw new Error(response.message || t('failedToLoadData'));
        }
      } catch (error) {
        console.error(t('failedToLoadData'), error);
        setError(error.message || t('failedToLoadDataRetry'));
      } finally {
        setLoading(false);
      }
    };
    
    loadUserStatistics();
  }, [language]); // 语言变化时重新加载数据
  
  // 格式化日期
  const formatDate = (date) => {
    if (!date) return t('notAvailable');
    
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return new Date(date).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', options);
  };
  
  if (loading) {
    return (
      <div className="home-loading">
        <div className="home-spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="home-error-card">
        <div className="home-error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <div className="home-error-message">{error}</div>
        <button className="dashboard-btn primary-btn" onClick={() => window.location.reload()}>
          {t('reload')}
        </button>
      </div>
    );
  }
  
  return (
    <div className="content-inner">
        {/* 页面标题 */}
        <div className="page-hd">
          <div>
            <h1 className="page-title">{t('userCenter')}</h1>
            <div className="page-sub">{t('dashboardSubtitle')}</div>
          </div>
        </div>
          
        {/* 统计卡片 - 使用设计稿类名 */}
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-icon">🔗</div>
            <div className="stat-label">{t('totalCloaks')}</div>
            <div className="stat-val">{stats.totalCloaks}</div>
            <div className="stat-sub">{t('totalCloaksSub').replace('{0}', stats.todayNewCloaks)}</div>
          </div>
            
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-label">{t('totalLogs')}</div>
            <div className="stat-val">{stats.totalLogs}</div>
            <div className="stat-sub">{t('totalLogsSub')}</div>
          </div>
            
          <div className="stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-label">{t('mySubscription')}</div>
            <div className="stat-val" style={{fontSize: '18px'}}>{stats.subscriptionStatus}</div>
            <div className="stat-sub">{formatDate(stats.subscriptionExpiry)}</div>
          </div>
            
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-label">{t('remainingCalls')}</div>
            <div className="stat-val">
              {stats.totalCalls === 0 ? t('unlimited') : stats.remainingCalls}
            </div>
            <div className="stat-sub">
              {stats.totalCalls === 0 ? '-' : `${stats.remainingCalls} / ${stats.totalCalls}`}
            </div>
          </div>
        </div>
        
        {/* 最近斗篷展示区域 - 使用设计稿表格样式 */}
        {recentCloaks.length > 0 && (
          <div className="table-wrap">
            <div className="table-toolbar">
              <span className="table-toolbar-title">{t('recentCloaks')}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('mode')}</th>
                  <th>{t('shortLink')}</th>
                  <th>{t('targetUrl')}</th>
                  <th>{t('visits')}</th>
                  <th>{t('created')}</th>
                </tr>
              </thead>
              <tbody>
                {recentCloaks.map((cloak, index) => (
                  <tr key={index}>
                    <td>{cloak.name}</td>
                    <td>
                      <span className={`badge ${
                        cloak.mode === 'cloak' ? 'badge-blue' :
                        cloak.mode === 'fullOpen' ? 'badge-purple' :
                        cloak.mode === 'audit' ? 'badge-orange' : 'badge-gray'
                      }`}>
                        {cloak.mode === 'cloak' ? t('cloakMode') :
                         cloak.mode === 'fullOpen' ? t('fullOpenMode') :
                         cloak.mode === 'audit' ? t('auditMode') :
                         t('unknown')}
                      </span>
                    </td>
                    <td>
                      <CopyableLink
                        url={`${cloak.shortLink}`}
                        label={cloak.shortLink}
                        onToast={showToast}
                        t={t}
                      />
                    </td>
                    <td>
                      <CopyableLink url={cloak.realUrl} label={cloak.realUrl} onToast={showToast} t={t} />
                    </td>
                    <td style={{textAlign: 'center'}}>{cloak.visits || 0}</td>
                    <td>{formatDate(cloak.created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{padding: '12px 16px', textAlign: 'center', borderTop: '1px solid var(--border)'}}>
              <a href="/dashboard/cloak/list" className="td-link">
                {t('viewAllCloaks')}
              </a>
            </div>
          </div>
        )}
        
        {toast.show ? (
          <Toast message={toast.message} type={toast.type} onClose={closeToast} />
        ) : null}
      </div>
  );
};

export default DashboardHome; 